import { useNavigate } from 'react-router-dom';

// Public (no-login) account & data deletion instructions.
// Required by Google Play Data Safety (MEA-136); complements the in-app
// deletion flow (Settings → Delete Account / DeleteAccountModal).
export default function DeleteAccountInfo() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-canvas to-canvas flex justify-center">
      <div className="w-full max-w-md px-6 py-8">
        <button
          onClick={() => navigate('/')}
          className="font-body text-lime text-sm mb-6 inline-flex items-center gap-1"
        >
          &larr; Back to Suppertide
        </button>

        <h1 className="font-display text-3xl font-semibold text-ink mb-2">
          Delete Your Account &amp; Data
        </h1>
        <p className="font-body text-ink-soft text-sm mb-6">
          Suppertide (operated by Sophinea) lets you permanently delete your
          account and all associated data at any time.
        </p>

        <div className="space-y-4">
          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Delete from inside the app
            </h2>
            <ol className="font-body text-ink-soft text-sm leading-relaxed list-decimal pl-4 space-y-1">
              <li>Open Suppertide and sign in.</li>
              <li>Go to the account menu and open <span className="font-semibold">Settings</span>.</li>
              <li>Tap <span className="font-semibold">Delete Account</span> and confirm.</li>
            </ol>
            <p className="font-body text-ink-soft text-sm leading-relaxed mt-2">
              Deletion is immediate and permanent &mdash; there is no recovery.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Can&rsquo;t access the app?
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              Email{' '}
              <a href="mailto:hello@suppertide.com" className="text-lime underline">
                hello@suppertide.com
              </a>{' '}
              from the email address on your account with the subject{' '}
              <span className="font-semibold">&ldquo;Delete my account&rdquo;</span>.
              We&rsquo;ll verify ownership and permanently delete your account and
              data within <span className="font-semibold">30 days</span>.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              What gets deleted
            </h2>
            <ul className="font-body text-ink-soft text-sm leading-relaxed list-disc pl-4 space-y-1">
              <li>Your login / authentication account</li>
              <li>Your family member profile</li>
              <li>
                If you are the last member of your household, the entire
                household and everything in it: recipes you created, meal plans,
                shopping data, swipe preferences, meal feedback, and all family
                member profiles (including children)
              </li>
              <li>Uploaded recipe images</li>
            </ul>
            <p className="font-body text-ink-soft text-sm leading-relaxed mt-2">
              Deletion cascades across our database, so no orphaned records
              remain. Routine backups are purged on our provider&rsquo;s standard
              rotation.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              What we keep
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              Nothing tied to your identity. Anonymous, aggregate usage analytics
              (which contain no personal information) are not linked to your
              account and are unaffected. See our{' '}
              <button
                onClick={() => navigate('/privacy')}
                className="text-lime underline"
              >
                Privacy Policy
              </button>{' '}
              for details.
            </p>
          </section>
        </div>

        <p className="font-body text-ink-soft text-xs text-center mt-6">
          &copy; 2026 Sophinea. All rights reserved.
        </p>
      </div>
    </div>
  );
}
